import React, {
  useState, useRef, useEffect, useCallback,
} from "react";

import { toast } from "react-toastify";

import {
  getPreferredEditedSampleId,
  getLatestTestResultsFromReportSpec,
  getTestResultsForSpecTooltip,
  resolveDisplaySampleId,
} from "../../../../Product/Report/Components/ReportSpecs/helper";
import ReportSearch from "../../../../Product/Report/ReportSearch";
import ReportsTable from "../../../../Product/Report/ReportsTable/ReportsTable";
import { useAccessStatus } from "../../../../grace-period/hooks/useAccessStatus";
import LoadingOverlay from "../../../LoadingOverlay";
import { getTemplateReportFields } from "../../helper";
import useTemplateAsyncAction from "../../hooks/useTemplateAsyncAction";
import { TEMPLATE_SIDES, useTemplateContext } from "../../TemplateContext";
import COALoadingPage from "../Common/COALoadingPage";
import Header from "../Common/Header";
import NoResultFound from "../Common/NoResultFound";
import Templates from "../Templates";

import { sortAndFilterProductReportFields } from "../../../../Product/Report/utils";

import { getEnvSpec } from "../../../../../actions/envAnalytics";
import { getEnvReports } from "../../../../../actions/envReports";
import {
  getTemplates,
} from "../../../../../actions/productTemplate";
import {
  getReports,
  getSpec,
} from "../../../../../actions/reports";

import "../styles/coa.css";
import "../styles/templates.css";
import "./COAReport.css";

const withCoaTabParams = (withSide, params = {}) => withSide({ coa_tab: true, ...params });

const appendCoaTabToPageLink = (pageLink) => {
  if (!pageLink || pageLink.includes("coa_tab=")) {
    return pageLink;
  }
  return `${pageLink}${pageLink.includes("?") ? "&" : "?"}coa_tab=true`;
};

export default function COAReport({
  handleAddCoa,
  isCreatingCoa,
  backToChooseCoaReport,
  handleCustomizeReportTemplate,
  linkReportKeys,
  setLinkReportKeys,
}) {
  const { withSide, side } = useTemplateContext();
  const { areResultsLocked } = useAccessStatus();
  const isEnvironmentSide = side === TEMPLATE_SIDES.ENVIRONMENTAL;
  const fetchCoaTabReports = (...args) => (
    isEnvironmentSide ? getEnvReports(...args) : getReports(...args)
  );
  const [isLoading, setIsLoading] = useState(true);
  const { loading: templateLoading, run: runTemplateListAction, abort: abortTemplateListAction } = useTemplateAsyncAction();
  const { loadingKey: templateSelectLoadingKey, run: runTemplateSelectAction } = useTemplateAsyncAction();
  const [reportSpecsLoading, setReportSpecsLoading] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [fields, setFields] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [filterByDateFrom, setFilterByDateFrom] = useState("");
  const [filterByDateTo, setFilterByDateTo] = useState("");
  const [nextPageLink, setNextPageLink] = useState("");
  const [totalPage, setTotalPage] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [loadingNewContent, setLoadingNewContent] = useState(false);
  const [templateList, setTemplateList] = useState([]);
  const [selectedReports, setSelectedReports] = useState({});
  const coaReportScrollbar = useRef(null);
  const [indexOfTemplateSelected, setIndexOfTemplateSelected] = useState(-1);
  // const [, setTemplateApiController] = useState(null);

  const fetchSpecTooltipData = useCallback(async (report) => {
    if (isEnvironmentSide) {
      const response = await getEnvSpec({
        sample_id: report.sample_id,
        location_id: report.location_id,
        received_date: report.received_date,
        sample_type: report.sample_type,
      }, true);
      if (response?.success) {
        return getTestResultsForSpecTooltip(response.spec);
      }
      return [];
    }
    const params = withSide({
      index: report.index,
      index_id: report.index_id,
      report_id: report.report_id,
      sample_id: report.sample_id,
      received_date: report.received_date,
      sample_type: report.sample_type,
    });
    const response = await getSpec(params, false);
    if (response?.success) {
      return getTestResultsForSpecTooltip(response.spec);
    }
    return [];
  }, [withSide, isEnvironmentSide]);

  /** Reset the template related states */
  const clearTemplateSelect = () => {
    setSelectedReports((prevState) => {
      const newState = { ...prevState };
      delete newState.template;
      setIndexOfTemplateSelected(-1);
      return newState;
    });
  };

  /**
   * Use the AbortController to cancel the last producttemplates/ call if the last call was still in progress.
   * @param {*} controller
   */
  // const abortPrevTemplateApiCall = (controller) => {
  //   setTemplateApiController((prevController) => {
  //     /** cancel the last api call */
  //     if (prevController) {
  //       prevController.abort();
  //     }
  //     /** delete the template field to aovid the user click the "Customize Report" button */
  //     setSelectedReports((prevState) => {
  //       const newState = { ...prevState };
  //       delete newState.template;
  //       return newState;
  //     });
  //     return controller;
  //   });
  // };

  /**
   * Make an api call to get the selected template information
   * @param {*} templateIndex
   */
  const saveTemplateIdForAddingCoa = (templateIndex) => {
    const templateId = templateList[templateIndex]?.template_id;
    return runTemplateSelectAction(async (signal) => {
      if (templateIndex === -1 || templateIndex === indexOfTemplateSelected) {
        clearTemplateSelect();
        return undefined;
      }
      setSelectedReports((prevState) => {
        const newState = { ...prevState };
        delete newState.template;
        return newState;
      });
      const params = withSide({ template_id: templateId });

      const response = await getTemplates(params, signal);
      if (response?.cancel || signal?.aborted) {
        return undefined;
      }
      if (response && response.success) {
        setIndexOfTemplateSelected(templateIndex);
        setSelectedReports((prevState) => ({
          ...prevState,
          template: response.result[0],
        }));
      } else if ((!response || !response.success) && !response?.cancel) {
        toast.error("Could not fetch template.");
        clearTemplateSelect();
      }
      return response;
    }, { useAbort: true, preventConcurrent: false, key: templateId });
  };

  const handleNavigation = () => {
    setIndexOfTemplateSelected(-1);
    backToChooseCoaReport(false);
  };

  useEffect(() => {
    const fetchReportsData = async () => {
      const fieldsData = await getTemplateReportFields(side);
      const filesData = await fetchCoaTabReports("", withCoaTabParams(withSide));
      if (!fieldsData || !fieldsData.success || !filesData || !filesData.success) {
        setFileList([]);
        setIsLoading(false);
        toast.error("Could not fetch reports data.");
        return;
      }

      setLinkReportKeys(fieldsData.link_report);
      const sortedFilteredFields = sortAndFilterProductReportFields(fieldsData.link_report, fieldsData.fields);
      const {
        files: newFiles, nextPageLink: newNextPageLink, totalPage: newTotalPage,
      } = filesData;

      setCurrentPage(1);
      setTotalPage(newTotalPage);
      setNextPageLink(newNextPageLink);
      setFileList([...newFiles]);
      setLoadingNewContent(false);
      setFields(sortedFilteredFields);
      setIsLoading(false);
    };

    const fetchTemplates = () => runTemplateListAction(async (signal) => {
      const response = await getTemplates(withSide(), signal);
      if (response?.cancel) {
        return undefined;
      }
      if (!response || !response.success) {
        toast.error("Could not fetch templates.");
        setTemplateList([]);
      } else {
        setTemplateList(response.result);
      }
      return response;
    }, { useAbort: true });

    fetchReportsData();
    fetchTemplates();
    return () => abortTemplateListAction();
  }, []); // eslint-disable-line

  useEffect(() => {
    if (loadingNewContent) {
      coaReportScrollbar.current.scrollToBottom();
    }
  }, [loadingNewContent]);

  useEffect(() => {
    if (!areResultsLocked) {
      return;
    }
    setSelectedReports((currentSelection) => (
      Object.keys(currentSelection).length ? {} : currentSelection
    ));
    setIndexOfTemplateSelected((currentIndex) => (
      currentIndex === -1 ? currentIndex : -1
    ));
  }, [areResultsLocked]);

  /**
   * If a user selects a sample, then make an api call to get the sample metadata.
   * @param {*} report
   * @param {*} fileIndex
   * @returns
   */
  const saveReportForAddingCoa = async (report, fileIndex) => {
    if (areResultsLocked) {
      return false;
    }

    setReportSpecsLoading(true);
    const newObj = {};
    let response;
    if (isEnvironmentSide) {
      response = await getEnvSpec({
        sample_id: report.sample_id,
        location_id: report.location_id,
        received_date: report.received_date,
        sample_type: report.sample_type,
      }, true);
    } else {
      const params = withSide({
        index: report.index,
        index_id: report.index_id,
        report_id: report.report_id,
        sample_id: report.sample_id,
        received_date: report.received_date,
        sample_type: report.sample_type,
      });
      response = await getSpec(params, true);
    }
    if (response && response.success) {
      const test_results = getLatestTestResultsFromReportSpec(response.spec);

      if (test_results.length === 0) {
        toast.info("This sample does not have any results.");
        setReportSpecsLoading(false);
        return false;
      }

      /** update states and push the selected sample information */
      const reportFromList = fileList[fileIndex];
      const {
        sample_type,
        received_date,
      } = reportFromList;
      // Live sample_id stays identity for API lookups; display uses edited ID when present
      const liveSampleId = reportFromList.sample_id;
      const displaySampleId = getPreferredEditedSampleId(response.spec)
        || resolveDisplaySampleId(reportFromList);

      const linkReportValuesObj = {};
      linkReportKeys.forEach((json_field) => { linkReportValuesObj[json_field] = reportFromList[json_field] ?? ""; }); // populate sample_data with linked fields vals, will be used in COASave
      newObj.sample_data = {
        sample_type,
        received_date,
        test_results,
        ...linkReportValuesObj,
        sample_id: displaySampleId,
        live_sample_id: liveSampleId,
      };
      newObj.report_index = fileIndex;
      setReportSpecsLoading(false);
      setSelectedReports((prevState) => {
        const newState = { ...prevState };
        if (newObj.sample_data) {
          newState[fileIndex] = newObj;
        }
        return newState;
      });
      return true;
    }
    setReportSpecsLoading(false);
    toast.error("Get sample report failed.");
    return false;
  };

  /**
   * A function which handles the on select action for each sample
   */
  const handleReportSelect = async (row, action) => {
    if (areResultsLocked) {
      return false;
    }

    if (action === "deselect") {
      /** only need to update the report states for unchecking a checkbox */
      setSelectedReports((prevState) => {
        const newState = { ...prevState };
        delete newState[row.index];
        return newState;
      });
      return true;
    }
    if (action === "select") {
      const resp = await saveReportForAddingCoa(fileList[row.index], row.index);
      return resp;
    }
    return false;
  };

  const handlePageChangeScroll = async () => {
    const loadedAllData = currentPage === totalPage;
    if (!loadedAllData && !loadingNewContent) {
      setLoadingNewContent(true);
      const nextPage = nextPageLink ? parseInt(nextPageLink.split("page=")[1].split("&")[0]) : 1;

      const resp = await fetchCoaTabReports(appendCoaTabToPageLink(nextPageLink));
      if (resp) {
        const {
          files: newFiles, nextPageLink: newNextPageLink, totalPage: newTotalPage,
        } = resp;
        setTotalPage(newTotalPage);
        setNextPageLink(newNextPageLink);
        setFileList([...fileList, ...newFiles]);
        setLoadingNewContent(false);
        setCurrentPage(nextPage);
      }
    }
  };

  const clearReportSelections = () => {
    setSelectedReports((prevState) => (
      prevState.template ? { template: prevState.template } : {}
    ));
  };

  const fetchFilteredReports = async ({
    search = searchValue,
    fromm = filterByDateFrom,
    to = filterByDateTo,
  } = {}) => {
    const params = withCoaTabParams(withSide, {
      fromm,
      to,
      specs_flag: "",
      search,
      retest: "",
    });
    const response = await fetchCoaTabReports("", params);
    if (!response || !response.success) {
      toast.error("Could not fetch reports data.");
      return;
    }
    clearReportSelections();
    const {
      files: newFiles, nextPageLink: newNextPageLink, totalPage: newTotalPage,
    } = response;
    if (coaReportScrollbar && coaReportScrollbar.current && coaReportScrollbar.current.scrollToTop()) {
      coaReportScrollbar.current.scrollToTop();
    }
    setTotalPage(newTotalPage);
    setNextPageLink(newNextPageLink);
    setFileList([...newFiles]);
    setLoadingNewContent(false);
    setCurrentPage(1);
    setSearchValue(search);
    setFilterByDateFrom(fromm);
    setFilterByDateTo(to);
  };

  const handleSearch = (value) => {
    fetchFilteredReports({ search: value });
  };

  const handleFilterByDate = (dateStart, dateEnd) => {
    fetchFilteredReports({ fromm: dateStart, to: dateEnd });
  };

  const getReportDates = async () => {
    const response = await fetchCoaTabReports("", withCoaTabParams(withSide, {
      specs_flag: "",
      search: searchValue,
      retest: "",
    }));
    if (response && response.success) {
      return response.dates ?? [];
    }
    return [];
  };

  /**
   * Convert the selectedReports to the datastructure { data_fields, samples, link_report_keys } and then pass it to the TemplateBuilder component
   */
  const handleCustomizeReport = () => {
    if (areResultsLocked) {
      return;
    }
    const data_fileds = {};
    const samples = [];

    Object.entries(selectedReports).forEach(([key, value]) => {
      if (key !== "template") {
        samples.push(value.sample_data);
        const reportInfo = fileList[value.report_index];
        /** push all the field values into the same field key set */
        Object.entries(reportInfo).forEach(([fieldName, fieldValue]) => {
          const resolvedFieldValue = fieldName === "sample_id"
            ? value.sample_data.sample_id
            : fieldValue;
          /** if the field value is valid and the field name does not exist, then set it to an empty array */
          if (typeof resolvedFieldValue === "string" && resolvedFieldValue.length > 0 && !data_fileds[fieldName]) {
            data_fileds[fieldName] = [];
          }
          /** push the field value if it is not existed in the array */
          if (data_fileds[fieldName] && !data_fileds[fieldName].includes(resolvedFieldValue) && resolvedFieldValue !== "") {
            data_fileds[fieldName].push(resolvedFieldValue);
          }
        });
      }
    });
    const reports = { data_fileds, samples, link_report_keys: linkReportKeys };
    if (Object.keys(reports).length > 0 && selectedReports.template) {
      handleCustomizeReportTemplate({ selectedReports: reports, selectedTemplate: selectedReports.template });
    }
  };

  if (isLoading || templateLoading) {
    return (
      <COALoadingPage page="COAReport" />
    );
  }

  return (
    <div className="coaContainer coaContainer--createReport TemplateModule">
      <div className="CoaReportWrap">
        <div className="CoaReportAndTemplate">
          <div className="coaReportContainer">
            { reportSpecsLoading && (<LoadingOverlay />)}
            <Header
              title="Your Reports"
              subtitle="Select a report and apply templates to create customised reports."
              handleAddCoa={handleAddCoa}
              handleNavigation={handleNavigation}
              isCreatingCoa={isCreatingCoa}
              showCreateReportButton={false}
            />
            <div className="CoaReportSearch">
              <ReportSearch
                flag="coa_reports"
                placeholder="Search..."
                handleSearch={handleSearch}
                handleFilterByDate={handleFilterByDate}
                getReportDates={getReportDates}
              />
            </div>
            {!isLoading && (
              fileList.length === 0
                ? <NoResultFound />
                : (
                  <ReportsTable
                    key={`${searchValue}|${filterByDateFrom}|${filterByDateTo}`}
                    wrapperClassName="COATableWrap"
                    fields={fields}
                    linkReportJsonFields={linkReportKeys}
                    setFields={setFields}
                    reports={fileList}
                    searchValToHighlight={searchValue}
                    loadingNewContent={loadingNewContent}
                    handleFetchNextPage={handlePageChangeScroll}
                    scrollbarRef={coaReportScrollbar}
                    loading={isLoading}
                    enableRowSelection
                    onRowSelection={handleReportSelect}
                    disableSampleResultsModalInteraction
                    disableTableHeaderInteraction
                    disableActions
                    enableSpecHoverTooltip
                    fetchSpecTooltipData={fetchSpecTooltipData}
                    sampleCategory={isEnvironmentSide ? "environment" : "product"}
                    specMethodReference={isEnvironmentSide}
                  />
                )
            )}
          </div>

          <div className="CoaReportAndTemplate__Template">
            <Templates
              templateList={templateList}
              reportIsSelected={Object.keys(selectedReports).length > 0}
              indexOfTemplateSelected={indexOfTemplateSelected}
              saveTemplateIdForAddingCoa={saveTemplateIdForAddingCoa}
              handleCustomizeReport={handleCustomizeReport}
              isReportAndTemplateSelected={Object.keys(selectedReports).length > 1 && selectedReports.template}
              selectLoadingKey={templateSelectLoadingKey}
              isCreatingCoa
            />
          </div>
        </div>
      </div>
    </div>
  );
}
